/**
 * 智能工具（AI / POD）生成结果自动写入图片空间。
 * 经后端 ingest-generated 落 OSS + compat 元数据，不重复扣 IMAGE_SPACE 豆（工具模块已计费）。
 */
import { imageSpaceAPI } from '@/utils/api';
import { resolveIngestSource } from '@/utils/imageSpaceIngestAspect';
import { runWithImageSpaceIngestContextAsync } from '@/utils/imageSpaceIngestContext';

const STUDIO_LABELS = {
  aiTextToImage: '文生图',
  img2img: '万能改图',
  aiOutpaint: 'AI扩图',
  imageFission: '图裂变',
  patternFission: '图案裂变',
  imageEnhance: '超分提质',
  smartCutout: '智能抠图',
  stampExtract: '印花提取',
  imageCrop: '图案裁剪',
  modelTryOn: 'AI模特试穿',
  modelHandheld: 'AI手持商品',
  modelPoseFission: 'AI姿势裂变',
  modelUniversalWear: 'AI万能穿戴',
  podProductDesign: 'POD商品设计',
  goods: '主图创作',
  textToVideo: '文生视频',
};

function sanitizeKey(value, maxLen = 48) {
  return String(value || '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, maxLen);
}

function extractTaskRowId(taskId) {
  const match = String(taskId || '').match(/-(\d+)(?:-|$)/);
  if (match) return match[1];
  const digits = String(taskId || '').replace(/\D/g, '');
  return digits.slice(-8) || '0';
}

export function buildSmartToolResultRecordId(navId, taskId, index, output = {}) {
  const oid = String(output?.sourceImageId || output?.imageId || '').trim();
  if (navId === 'smartCutout' && oid) {
    return `cutout_${extractTaskRowId(taskId)}_${oid.slice(-6)}`;
  }
  const safeTask = sanitizeKey(taskId, 40);
  const idx = Number.isFinite(Number(output?.index))
    ? Number(output.index)
    : (Number.isFinite(Number(output?.sourceIndex)) ? Number(output.sourceIndex) : index);
  return `${sanitizeKey(navId, 24)}-${safeTask}-${idx}`;
}

/**
 * 详情页重命名/收藏用：列出可能的图片空间 id（含历史后端命名兼容）。
 * 旧后端 img2img 曾用 `img2img-{taskId}-ref-{index}-0`。
 */
export function listStudioOutputImageSpaceIdCandidates(navId, taskId, output = {}, index = 0) {
  const ids = [];
  const push = (value) => {
    const id = String(value || '').trim();
    if (!id || ids.includes(id)) return;
    ids.push(id);
  };
  push(output?.imageSpaceId || output?.spaceId);
  push(buildSmartToolResultRecordId(navId, taskId, index, output));
  const toolNavId = String(navId || '').trim();
  const safeTask = String(taskId || '').trim();
  if (toolNavId === 'img2img' && safeTask) {
    const idx = Number.isFinite(Number(output?.index))
      ? Number(output.index)
      : (Number.isFinite(Number(output?.sourceIndex)) ? Number(output.sourceIndex) : index);
    const legacyRequestId = `${safeTask}-ref-${idx}`;
    push(`img2img-${sanitizeKey(legacyRequestId, 64)}-0`);
  }
  return ids;
}

function buildResultName(navId, index, output = {}) {
  const label = STUDIO_LABELS[navId] || navId || '生成图';
  const seq = Number.isFinite(Number(output?.sourceIndex))
    ? Number(output.sourceIndex) + 1
    : index + 1;
  return `${label}-${seq}`;
}

/** 详情/历史：解析输出图对应的图片空间记录 id（入库时确定性生成） */
export function resolveStudioOutputImageSpaceId(navId, taskId, output, index = 0) {
  const candidates = listStudioOutputImageSpaceIdCandidates(navId, taskId, output || {}, index);
  return candidates[0] || '';
}

/** 详情「重命名」弹窗初始名：优先已入库名称 */
export function resolveStudioOutputImageSpaceName(navId, output, index = 0) {
  const explicit = String(output?.name || output?.imageSpaceName || '').trim();
  if (explicit) return explicit;
  return buildResultName(navId, index, output || {});
}

function normalizeOutputRows(urls, outputs) {
  if (Array.isArray(outputs) && outputs.length > 0) {
    const expanded = [];
    outputs
      .filter((row) => row && row.status !== 'failed')
      .forEach((row, index) => {
        const nestedUrls = Array.isArray(row.urls)
          ? row.urls.map((url) => String(url || '').trim()).filter(Boolean)
          : [];
        const fallbackUrl = String(row.url || row.outputUrl || row.imageUrl || '').trim();
        const rowUrls = nestedUrls.length > 0 ? nestedUrls : (fallbackUrl ? [fallbackUrl] : []);
        rowUrls.forEach((url) => {
          expanded.push({
            url,
            output: {
              ...row,
              url,
              outputUrl: url,
              imageUrl: url,
              index: expanded.length,
            },
            index: expanded.length,
          });
        });
      });
    if (expanded.length > 0) return expanded;
  }
  return (Array.isArray(urls) ? urls : [])
    .map((url, index) => ({
      url: String(url || '').trim(),
      output: {},
      index,
    }))
    .filter((row) => row.url);
}

/**
 * @param {object} params
 * @param {string} params.navId 工作台 navId / 任务类型
 * @param {string} [params.taskId]
 * @param {string[]} [params.urls]
 * @param {object[]} [params.outputs]
 * @param {string} [params.idPrefix]
 * @param {string} [params.explicitSource]
 */
export async function persistSmartToolResultsToImageSpace({
  navId,
  taskId = '',
  urls = [],
  outputs = [],
  idPrefix,
  explicitSource,
} = {}) {
  const toolNavId = String(navId || '').trim();
  if (!toolNavId) return [];

  const rows = normalizeOutputRows(urls, outputs);
  if (rows.length === 0) return [];

  return runWithImageSpaceIngestContextAsync(
    { navId: toolNavId, workflowStepNavId: toolNavId, ingestKind: 'result' },
    async () => {
      const source = explicitSource || resolveIngestSource({ ingestKind: 'result' });
      const items = rows.map(({ url, output, index }) => ({
        url,
        name: buildResultName(toolNavId, index, output),
        id: buildSmartToolResultRecordId(toolNavId, taskId, index, output),
        taskId: String(taskId || '').trim() || undefined,
        width: output?.width ?? null,
        height: output?.height ?? null,
      }));

      try {
        const response = await imageSpaceAPI.ingestGenerated({
          source,
          idPrefix: idPrefix || toolNavId,
          taskId: String(taskId || '').trim() || undefined,
          items,
        });
        return Array.isArray(response?.images) ? response.images : [];
      } catch (error) {
        console.warn('[图片空间] 智能工具结果自动入库失败', toolNavId, taskId, error);
        return [];
      }
    },
  );
}

export function mergePersistedImageSpaceItems(saved, onMergeImageSpaceItems) {
  if (!Array.isArray(saved) || saved.length === 0) return;
  if (typeof onMergeImageSpaceItems !== 'function') return;
  onMergeImageSpaceItems(saved.map((item) => ({
    id: item.id,
    name: item.name,
    url: item.url || item.src,
    src: item.src || item.url,
    viewUrl: item.viewUrl || item.storedViewUrl || '',
    displayUrl: item.displayUrl || item.urlDisplay || item.storedUrlDisplay || '',
    storedViewUrl: item.storedViewUrl || '',
    storedUrlDisplay: item.storedUrlDisplay || '',
    source: item.source || item.sourceType,
    sourceType: item.sourceType || item.source,
    width: item.width,
    height: item.height,
    size: item.size,
    createdAt: item.createdAt,
    uploadedAt: item.uploadedAt,
  })));
}
